<template>
  <v-dialog v-model="localShow" :max-width="800" :persistent="true" :fullscreen="isMobile">
    <v-card v-bind:style="{ 'min-height': !isMobile ? '550px' : '' }">
      <v-card-title class="pa-4 d-flex align-center" v-if="documentType">
        <v-icon start>mdi-send</v-icon> Replace {{ documentType.name }}
        <v-spacer />
        <v-btn icon variant="text" @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-form ref="form" class="pl-4">
        <v-card-text>
          <v-row>
            <v-col>
              <div
                class="file-uploader"
                @dragover.prevent="onDragOver"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onDrop"
                :class="{ 'drag-over': isDragOver }"
                data-cy="drag-and-drop-zone"
              >
                <input type="file" ref="fileInput" class="file-input" @change="onFileSelected" />
                <div class="file-uploader-content" @click="$refs.fileInput.click()">
                  <span class="dropzone-custom-title">
                    <v-icon start>mdi-upload</v-icon>
                    <span>Select a file or drag it here</span>
                  </span>
                </div>
                <div v-if="selectedFile" class="selected-file-name mt-2">
                  {{ selectedFile.name }}
                  <a @click.stop="removeFile" class="ml-2"><v-icon size="small">mdi-close</v-icon></a>
                </div>
              </div>
              <v-progress-linear indeterminate v-if="loading"></v-progress-linear>
            </v-col>
          </v-row>
          <v-row class="pt-4">
            <v-spacer />
            <v-btn class="mr-3" variant="flat" color="primary" @click="next()" data-cy="next">
              Next
              <v-icon end>mdi-arrow-right</v-icon>
            </v-btn>
          </v-row>
        </v-card-text>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import batchService from "@/services/batchService";
import { mapGetters } from "vuex";
import httpService from "@/services/httpService";
import Auth0Mixin from "../views/Auth0Mixin";
import responsiveService from "@/services/responsiveService";

const requiredRule = [v => !!v || "Required"];

export default {
  mixins: [Auth0Mixin],

  props: {
    show: Boolean,
    documentType: { type: Object, default: () => ({ name: "Invoice " }) },
    cancel: Function,
    receivingPartyId: Number,
    onReplaceFile: Function,
    replaceDocumentId: { type: Number, default: -1 },
  },

  emits: ["update:show"],
  data() {
    return {
      localShow: this.show,
      loading: false,
      focusOnParty: true,
      requiredRule,
      selectedFile: null,
      isDragOver: false,
      fileUploadData: {
        batchName: null,
        userId: null,
        folderId: 1,
      },
    };
  },

  computed: {
    ...mapGetters("auth0Store", ["user"]),

    disableNext() {
      return this.loading;
    },

    isMobile() {
      return responsiveService.isMobile(this.$vuetify.display);
    },
  },

  watch: {
    show(val) {
      this.localShow = val;
      this.focusOnParty = val;
      if (val) this.removeFile();
    },
    localShow(val) {
      if (!val) this.$emit("update:show", val);
    },

    documentTypeId() {
      this.init();
    },
  },

  methods: {
    async init() {
      // No additional initialization needed
    },

    onDragOver() {
      this.isDragOver = true;
    },
    onDragLeave() {
      this.isDragOver = false;
    },
    onDrop(event) {
      this.isDragOver = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.selectedFile = files[0];
      }
    },
    onFileSelected(event) {
      const files = event.target.files;
      if (files.length > 0) {
        this.selectedFile = files[0];
      }
    },
    removeFile() {
      this.selectedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    async next() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      if (!this.selectedFile) {
        this.$root.$snackbar.error("Please select a file to send");
        return;
      }

      this.fileUploadData.batchName = batchService.generateBatchName();
      this.fileUploadData.receivingPartyId = this.receivingPartyId;
      this.fileUploadData.documentTypeId = this.documentType.id;
      this.fileUploadData.userId = this.user.id;

      this.loading = true;
      this.uploadFile();
    },

    async uploadFile() {
      try {
        await httpService.postFile("/documents/upload", this.selectedFile, { fileUploadData: this.fileUploadData });
        await this.completeSending();
      } catch (error) {
        this.$root.$snackbar.error("File upload failed");
        this.loading = false;
      }
    },

    async completeSending() {
      if (!this.fileUploadData.batchName) return;

      let docIds = await batchService.getBatchDocumentIds(this.fileUploadData.batchName, this.fileUploadData.userId);
      this.onReplaceFile(docIds[0]);
      this.loading = false;
      this.cancel();
    },
  },
};
</script>
<style scoped>
.file-uploader {
  position: relative;
  cursor: pointer;
  border: 2px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  padding: 16px;
  text-align: center;
  transition: border-color 0.2s;
}
.file-uploader:hover,
.file-uploader.drag-over {
  border-color: rgb(var(--v-theme-primary));
}
.file-uploader .file-input {
  display: none;
}
.file-uploader-content {
  cursor: pointer;
}
.selected-file-name {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.6);
}
</style>
